import { z } from 'zod';

/**
 * Tema da interface do cockpit.
 *
 * Mora no domínio compartilhado porque a escolha viaja: o cockpit grava a preferência na
 * API e a lê de volta em outro navegador. Nome de tema divergente entre as duas pontas
 * viraria preferência gravada que a tela não sabe aplicar.
 *
 *  claro   — fundo de pergaminho, tinta escura
 *  escuro  — a paleta original: ouro sobre tinta
 *  sistema — acompanha o que o sistema operacional pede
 */
export const TEMAS = ['sistema', 'claro', 'escuro'] as const;
export const temaSchema = z.enum(TEMAS);
export type Tema = (typeof TEMAS)[number];

/** O tema que de fato pinta a tela. "sistema" nunca chega até o CSS. */
export type TemaAplicado = 'claro' | 'escuro';

/**
 * Escuro, e não "sistema", para quem nunca escolheu.
 *
 * A plataforma nasceu escura e é assim que a operação a conhece. Estrear seguindo o
 * sistema trocaria a tela de quem não pediu nada — a mudança tem que ser escolhida.
 */
export const TEMA_PADRAO: Tema = 'escuro';

export const ROTULO_DO_TEMA: Record<Tema, string> = {
  sistema: 'Sistema',
  claro: 'Claro',
  escuro: 'Escuro',
};

/** Resolve a escolha para o tema que será pintado, dado o que o sistema operacional pede. */
export function temaAplicado(escolha: Tema, doSistema: TemaAplicado): TemaAplicado {
  return escolha === 'sistema' ? doSistema : escolha;
}

/**
 * Valida um tema vindo de fora do sistema de tipos — o `localStorage` do navegador, uma
 * resposta antiga da API. Valor desconhecido vira `null`, e quem chama decide o padrão:
 * uma preferência ilegível não pode deixar a tela sem tema nenhum.
 */
export function temaValido(bruto: unknown): Tema | null {
  const resultado = temaSchema.safeParse(bruto);
  return resultado.success ? resultado.data : null;
}

/** Preferências pessoais do operador. Hoje só o tema; o formato já é um objeto para crescer. */
export interface PreferenciasDoOperador {
  tema: Tema;
}

/**
 * O que a leitura devolve, com uma informação a mais que não é gravada: se **alguém já
 * escolheu**.
 *
 * Sem isso, o cockpit não consegue distinguir "esta conta prefere escuro" de "esta conta
 * nunca disse nada, e escuro é o padrão" — e passaria por cima da escolha que a pessoa
 * acabou de fazer na tela de login, que é justamente quando ela mais reparou no tema.
 */
export interface PreferenciasLidas extends PreferenciasDoOperador {
  definido: boolean;
}

export const preferenciasSchema = z.object({ tema: temaSchema }).strict();

export const PREFERENCIAS_PADRAO: PreferenciasDoOperador = { tema: TEMA_PADRAO };
